import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TwoFASetup } from "@/components/TwoFASetup";

type SignupFlow = "form" | "loading" | "backup_download" | "success";

export default function Signup() {
  const navigate = useNavigate();
  const [signupFlow, setSignupFlow] = useState<SignupFlow>("form");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [backupFile, setBackupFile] = useState<string | null>(null);
  const [backupFileName, setBackupFileName] = useState<string>("");
  const [userId, setUserId] = useState<string>("");

  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showPasswordStep, setShowPasswordStep] = useState(false);

  const [show2FA, setShow2FA] = useState(false);

  const handleCopyBackup = () => {
    if (!backupFile) return;
    navigator.clipboard.writeText(backupFile);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBackupConfirmed = () => {
    setShow2FA(true);
  };

  const handle2FAComplete = () => {
    setShow2FA(false);
    setSignupFlow("success");
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  const handleSignupWithGoogle = () => {
    toast({
      title: "COMING SOON",
      description: "Google signup will be available soon.",
    });
  };

  const handleSignupWithTelegram = () => {
    toast({
      title: "COMING SOON",
      description: "Telegram signup will be available soon.",
    });
  };

  const handleContinue = () => {
    if (!email.trim()) {
      setErrorMsg("EMAIL IS REQUIRED");
      return;
    }
    setShowPasswordStep(true);
    setErrorMsg("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);
    setSignupFlow("loading");

    try {
      const response = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || error.message || "Signup failed");
      }

      const data = await response.json();

      const backupString = data.backupFile;
      if (!backupString) {
        throw new Error("Backup data missing from server response");
      }

      setBackupFile(backupString);
      setBackupFileName(`fixorium_backup_${email.split("@")[0]}.json`);
      setUserId(data.userId);
      setSignupFlow("backup_download");

      toast({ title: "Account created", description: "Please save your backup." });
    } catch (err: any) {
      setErrorMsg(err.message || "Failed to create account");
      setSignupFlow("form");
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setSignupFlow("form");
    setBackupFile(null);
    setErrorMsg("");
    setShowPasswordStep(false);
    setEmail("");
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        
        {/* SIGNUP FORM */}
        {signupFlow === "form" && (
          <form onSubmit={showPasswordStep ? handleSubmit : (e) => e.preventDefault()} className="space-y-5">
            <h2 className="text-2xl font-bold text-foreground">SIGNUP</h2>
            
            {/* Email Input */}
            <div>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={!showPasswordStep}
                disabled={showPasswordStep}
                className="w-full bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground focus:border-yellow-500 transition-all duration-300 rounded-none px-0 py-2"
                placeholder="Email Address"
              />
            </div>

            {/* Continue Button */}
            {!showPasswordStep && (
              <Button
                type="button"
                onClick={handleContinue}
                disabled={loading || !email.trim()}
                className="w-full h-12 text-base bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg"
              >
                CONTINUE
              </Button>
            )}

            {/* OR Separator */}
            {!showPasswordStep && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-background text-muted-foreground uppercase text-xs">OR</span>
                </div>
              </div>
            )}

            {/* Google Button */}
            {!showPasswordStep && (
              <Button
                type="button"
                onClick={handleSignupWithGoogle}
                variant="outline"
                className="w-full h-12 text-base border-border text-foreground hover:bg-muted rounded-lg"
              >
                SIGNUP WITH GOOGLE
              </Button>
            )}

            {/* Telegram Button */}
            {!showPasswordStep && (
              <Button
                type="button"
                onClick={handleSignupWithTelegram}
                variant="outline"
                className="w-full h-12 text-base border-border text-foreground hover:bg-muted rounded-lg"
              >
                SIGNUP WITH TELEGRAM
              </Button>
            )}

            {/* Password Step */}
            {showPasswordStep && (
              <>
                <div>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground focus:border-yellow-500 transition-all duration-300 rounded-none px-0 py-2 pr-10"
                      placeholder="Password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {errorMsg && <p className="text-red-500 text-sm">{errorMsg}</p>}

                {/* Sign Up Button */}
                <Button 
                  type="submit" 
                  disabled={loading || !password} 
                  className="w-full h-12 text-base bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  SIGN UP
                </Button>

                {/* OR Separator */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-background text-muted-foreground uppercase text-xs">OR</span>
                  </div>
                </div>

                {/* Google Button - Password step */}
                <Button
                  type="button"
                  onClick={handleSignupWithGoogle}
                  variant="outline"
                  className="w-full h-12 text-base border-border text-foreground hover:bg-muted rounded-lg"
                >
                  SIGNUP WITH GOOGLE
                </Button>

                {/* Telegram Button - Password step */}
                <Button
                  type="button"
                  onClick={handleSignupWithTelegram}
                  variant="outline"
                  className="w-full h-12 text-base border-border text-foreground hover:bg-muted rounded-lg"
                >
                  SIGNUP WITH TELEGRAM
                </Button>

                {/* Back to email */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordStep(false);
                      setPassword("");
                      setErrorMsg("");
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    ← BACK TO EMAIL
                  </button>
                </div>
              </>
            )}

            {/* Login link */}
            {!showPasswordStep && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  ALREADY HAVE AN ACCOUNT?{" "}
                  <button type="button" onClick={handleGoToLogin} className="text-yellow-500 hover:text-yellow-600 font-semibold transition-colors">
                    LOGIN
                  </button>
                </p>
              </div>
            )}
          </form>
        )}

        {/* LOADING STATE */}
        {signupFlow === "loading" && (
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mx-auto" />
            <p className="text-foreground text-lg">CREATING FIXORIUM ACCOUNT</p>
          </div>
        )}

        {/* BACKUP / SECURITY KEY STATE */}
        {signupFlow === "backup_download" && (
          <div className="space-y-4">
            {!backupFile ? (
              <div className="text-center space-y-4">
                <p className="text-red-500">Backup data is missing. Please try again.</p>
                <Button onClick={handleRetry} className="w-full bg-yellow-500 text-black hover:bg-yellow-600">
                  ← GO BACK & RETRY
                </Button>
              </div>
            ) : (
              <>
                <p className="text-foreground text-center text-sm uppercase">
                  YOUR UNIQUE SECURITY KEY IS SHOWN BELOW. SAVE IT SOMEWHERE SAFE.
                </p>

                <textarea
                  readOnly
                  value={backupFile}
                  className="w-full bg-muted text-foreground p-3 rounded text-xs font-mono border border-border resize-none focus:outline-none focus:border-yellow-500"
                  rows={8}
                />

                <Button onClick={handleCopyBackup} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
                  {copied ? "✓ COPIED!" : "COPY SECURITY KEY"}
                </Button>

                <Button onClick={handleBackupConfirmed} className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold">
                  CONTINUE (I HAVE STORED THE KEY)
                </Button>
              </>
            )}
          </div>
        )}

        {/* SUCCESS STATE */}
        {signupFlow === "success" && (
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-bold text-foreground">ACCOUNT CREATED</h2>
            <Button onClick={handleGoToLogin} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
              CONTINUE TO LOGIN
            </Button>
          </div>
        )}
      </div>

      {/* 2FA Setup Modal */}
      {show2FA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <TwoFASetup userId={userId} onComplete={handle2FAComplete} />
        </div>
      )}
    </div>
  );
}
